import { useEffect, useState } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { rememberAlbumKey } from '../albums/albumKeys.js';
import { rotateAlbumPhotos, type RotateProgress } from './rotateAlbumKey.js';

interface Props {
  rootAlbumId: string;
  // Current K_album for this root. Needed both as "old key" during rotation
  // and as a sanity check (we'll compare against what the server says).
  currentAlbumKey: CryptoKey;
  // Notify parent so it can reload the album/photo state after a successful
  // rotation (the album's wrappedAlbumKey on the server has changed).
  onRotated?: () => void;
}

interface RecipientChoice {
  recipientUserId: string;
  recipientEmail: string;
  // Already pre-selected for "keep" if true at mount time. Maps to whether
  // the share row already has wrappedAlbumKeyNext (resume of a prior init).
  initiallyKept: boolean;
  keep: boolean;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; recipients: RecipientChoice[]; rotationInProgress: boolean }
  | { kind: 'running'; progress: RotateProgress }
  | { kind: 'done'; sharesKept: number; sharesRevoked: number };

export function RotationSection({ rootAlbumId, currentAlbumKey, onRotated }: Props) {
  const session = useAuthSession();
  const { metadata, storage } = useInfra();
  const [state, setState] = useState<State>({ kind: 'idle' });

  async function loadRecipients() {
    setState({ kind: 'loading' });
    try {
      const out = await metadata.listOutgoingShares(rootAlbumId, session.jwt);
      // Detect resume: if any share already has wrappedAlbumKeyNext set, a
      // rotation is in flight on the server. We surface a "Resume" CTA in
      // that case and pre-mark those recipients as kept.
      let rotationInProgress = false;
      const recipients: RecipientChoice[] = out.shares.map((s) => {
        const initiallyKept = Boolean(s.wrappedAlbumKeyNext);
        if (initiallyKept) rotationInProgress = true;
        return {
          recipientUserId: s.recipientUserId,
          recipientEmail: s.recipientEmail,
          initiallyKept,
          keep: initiallyKept,
        };
      });
      // If init landed but no recipient was kept (sole owner), the album
      // itself still carries wrappedAlbumKeyNext — detect via /pending.
      if (!rotationInProgress) {
        try {
          await metadata.rotationPending(rootAlbumId, session.jwt);
          rotationInProgress = true;
        } catch {
          // No rotation in progress — expected on the happy path. /pending
          // returns 403 NO_ROTATION when nothing is pending.
        }
      }
      setState({ kind: 'ready', recipients, rotationInProgress });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'errore' });
    }
  }

  useEffect(() => {
    void loadRecipients();
  }, [rootAlbumId]);

  function toggleKeep(recipientUserId: string) {
    setState((s) => {
      if (s.kind !== 'ready') return s;
      return {
        ...s,
        recipients: s.recipients.map((r) =>
          r.recipientUserId === recipientUserId ? { ...r, keep: !r.keep } : r,
        ),
      };
    });
  }

  async function startRotation(recipients: RecipientChoice[], resume: boolean) {
    setState({ kind: 'running', progress: { total: 0, rotated: 0 } });
    try {
      // Generate a fresh K_album on this device. The new key never leaves
      // the browser in cleartext: we wrap it for the owner's RSA pub and
      // for each kept recipient's RSA pub.
      const newAlbumKey = await cv.generateAlbumKey();

      if (!resume) {
        const wrappedForOwner = await cv.wrapAlbumKeyFor(newAlbumKey, session.rsaPubB64);
        const recipientWraps: dto.RotateInitRequest['recipientWraps'] = [];
        for (const r of recipients.filter((x) => x.keep)) {
          // Re-lookup the recipient to get their current rsaPub. Cheap and
          // avoids assuming the cached pub on the share row is still valid.
          const u = await metadata.lookupUserByEmail(r.recipientEmail, session.jwt);
          const wrappedForRecipient = await cv.wrapAlbumKeyFor(newAlbumKey, u.rsaPubB64);
          recipientWraps.push({
            recipientUserId: r.recipientUserId,
            wrappedAlbumKey: wrappedForRecipient,
          });
        }
        await metadata.rotateInit(
          rootAlbumId,
          { wrappedAlbumKeyForOwner: wrappedForOwner, recipientWraps },
          session.jwt,
        );
      } else {
        // Resume path: the album already carries wrappedAlbumKeyNext from a
        // prior init. We can't recover the K_new the previous tab generated
        // — the wrappedAlbumKeyNext is opaque to us until we unwrap it.
        const pending = await metadata.rotationPending(rootAlbumId, session.jwt);
        const resumed = await cv.unwrapAlbumKey(pending.wrappedAlbumKeyNext, session.rsaPriv);
        // Replace newAlbumKey with the resumed one. We can't reassign the
        // const above; instead we just pass `resumed` below.
        return await runRotation(resumed);
      }
      return await runRotation(newAlbumKey);
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'errore' });
    }
  }

  async function runRotation(newAlbumKey: CryptoKey): Promise<void> {
    const { commit } = await rotateAlbumPhotos({
      rootId: rootAlbumId,
      oldAlbumKey: currentAlbumKey,
      newAlbumKey,
      token: session.jwt,
      metadata,
      storage,
      onProgress: (progress) => setState({ kind: 'running', progress }),
    });
    // The server has swapped wrappedAlbumKey to the new gen. Update the
    // module-level K_album cache so subsequent reads in this tab don't try
    // to unwrap with rsaPriv against the old wrapped key.
    rememberAlbumKey(rootAlbumId, newAlbumKey);
    setState({
      kind: 'done',
      sharesKept: commit.sharesKept,
      sharesRevoked: commit.sharesRevoked,
    });
    onRotated?.();
  }

  return (
    <Card className="flex flex-col gap-4 border-cyan/40">
      <div className="flex items-center gap-3">
        <Icon name="autorenew" className="text-2xl text-cyan" />
        <h3 className="font-display text-lg font-semibold">Ruota chiave album</h3>
      </div>
      <p className="text-xs text-on-surface-variant">
        Genera una nuova chiave per questa radice e re-cifra tutte le foto. I destinatari non
        selezionati sotto perdono l'accesso anche ai blob già scaricati offline (revoca
        crittograficamente forte). Operazione lunga: non chiudere la pagina.
      </p>

      {state.kind === 'loading' ? (
        <p className="text-xs text-on-surface-variant">Carico…</p>
      ) : state.kind === 'error' ? (
        <p className="text-xs text-error">Errore: {state.message}</p>
      ) : state.kind === 'ready' ? (
        <>
          {state.recipients.length === 0 ? (
            <p className="text-xs text-on-surface-variant">
              Nessuno share attivo: la rotazione cambierà solo la tua chiave.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {state.recipients.map((r) => (
                <li
                  key={r.recipientUserId}
                  className="flex items-center justify-between rounded border border-outline px-3 py-2 text-sm"
                >
                  <span>{r.recipientEmail}</span>
                  <label className="flex cursor-pointer items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={r.keep}
                      onChange={() => toggleKeep(r.recipientUserId)}
                      disabled={state.rotationInProgress && r.initiallyKept}
                    />
                    Mantieni
                  </label>
                </li>
              ))}
            </ul>
          )}
          {state.rotationInProgress ? (
            <>
              <p className="text-xs text-on-surface-variant">
                Una rotazione è già in corso su questa radice (forse interrotta da un altro tab).
                Riprendi per completare con la chiave già emessa.
              </p>
              <Button
                onClick={() => void startRotation(state.recipients, true)}
                leftIcon={<Icon name="play_arrow" />}
                className="self-start"
              >
                Riprendi rotazione
              </Button>
            </>
          ) : (
            <Button
              onClick={() => void startRotation(state.recipients, false)}
              leftIcon={<Icon name="autorenew" />}
              className="self-start"
            >
              Inizia rotazione
            </Button>
          )}
        </>
      ) : state.kind === 'running' ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            Rotazione in corso: {state.progress.rotated} / {state.progress.total}
            {state.progress.current ? ` — ${labelForPhase(state.progress.current.phase)}` : ''}
          </p>
          {state.progress.total > 0 ? (
            <div className="h-2 w-full overflow-hidden rounded bg-surface-container-low">
              <div
                className="h-full bg-cyan transition-[width] duration-200"
                style={{
                  width: `${Math.round((state.progress.rotated / state.progress.total) * 100)}%`,
                }}
              />
            </div>
          ) : null}
        </div>
      ) : state.kind === 'done' ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-cyan">
            Rotazione completata. {state.sharesKept} share aggiornati, {state.sharesRevoked}{' '}
            revocati.
          </p>
          <Button onClick={() => void loadRecipients()} className="self-start">
            Aggiorna
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function labelForPhase(phase: 'downloading' | 'reencrypting' | 'uploading'): string {
  switch (phase) {
    case 'downloading':
      return 'scarico';
    case 'reencrypting':
      return 'ri-cifro';
    case 'uploading':
      return 'carico';
  }
}
